import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProperty } from "@/lib/property";
import { useTheme } from "@/lib/theme";
import { api, getOfflineQueueSize, flushOfflineQueue } from "@/lib/api";
import { outboxAll } from "@/lib/offlineDb";
import { toast } from "sonner";
import {
  MountainsIcon, GaugeIcon, BuildingsIcon, DoorIcon, CalendarBlankIcon,
  UsersIcon, ReceiptIcon, BroomIcon, UserGearIcon, ChartLineIcon, GearSixIcon, SignOutIcon,
  RowsIcon, CurrencyInrIcon, PlugsConnectedIcon, WrenchIcon, CrownSimpleIcon, MoonStarsIcon, SunIcon,
  CalendarCheckIcon, MoneyIcon, WalletIcon, CloudArrowUpIcon, CloudSlashIcon, QuestionIcon,
  ForkKnifeIcon, ClockCounterClockwiseIcon, ArmchairIcon, SparkleIcon,
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { NAV, AUTH } from "@/constants/testIds";
import { usePermissions } from "@/lib/permissions";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: GaugeIcon, tid: NAV.dashboard, section: "dashboard" },
  { to: "/timeline", label: "Front Desk", icon: RowsIcon, tid: NAV.timeline, section: "front_desk" },
  { to: "/reservations", label: "Reservations", icon: CalendarBlankIcon, tid: NAV.reservations, section: "reservations" },
  { to: "/rates", label: "Rate Calendar", icon: CurrencyInrIcon, tid: NAV.rates, section: "rate_calendar" },
  { to: "/rooms", label: "Rooms", icon: DoorIcon, tid: NAV.rooms, section: "rooms" },
  { to: "/guests", label: "Guests", icon: UsersIcon, tid: NAV.guests, section: "guests" },
  { to: "/housekeeping", label: "Housekeeping", icon: BroomIcon, tid: NAV.housekeeping, section: "housekeeping" },
  { to: "/my-tasks", label: "My Tasks (mobile)", icon: BroomIcon, tid: "nav-my-tasks", section: "housekeeping" },
  { to: "/invoices", label: "Invoices", icon: ReceiptIcon, tid: NAV.invoices, section: "invoices" },
  { to: "/quotations", label: "Quotations", icon: ReceiptIcon, tid: "nav-quotations", section: "invoices" },
  { to: "/channels", label: "Channels", icon: PlugsConnectedIcon, tid: NAV.channels, section: "channels" },
  { to: "/properties", label: "Properties", icon: BuildingsIcon, tid: NAV.properties, section: "properties" },
  { to: "/staff", label: "Staff", icon: UserGearIcon, tid: NAV.staff, section: "staff" },
  { to: "/attendance", label: "Attendance", icon: CalendarCheckIcon, tid: "nav-attendance", section: "attendance" },
  { to: "/payroll", label: "Payroll", icon: MoneyIcon, tid: "nav-payroll", section: "payroll" },
  { to: "/expenses", label: "Expenses", icon: WalletIcon, tid: "nav-expenses", section: "expenses" },
  { to: "/pos", label: "Restaurant POS", icon: ForkKnifeIcon, tid: "nav-pos", section: "pos" },
  { to: "/halls", label: "Halls & Events", icon: ArmchairIcon, tid: "nav-halls", section: "halls" },
  { to: "/amenities", label: "Amenities", icon: SparkleIcon, tid: "nav-amenities", section: "amenities" },
  { to: "/reports", label: "Reports", icon: ChartLineIcon, tid: NAV.reports, section: "reports" },
  { to: "/audit", label: "Audit history", icon: ClockCounterClockwiseIcon, tid: "nav-audit", section: "audit" },
  { to: "/help", label: "Help", icon: QuestionIcon, tid: "nav-help", section: "help" },
  { to: "/admin", label: "Admin Console", icon: WrenchIcon, tid: "nav-admin", section: "admin" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { properties, activeId, setActive } = useProperty();
  const { theme, toggle } = useTheme();
  const { sections: permSections } = usePermissions();
  const nav = useNavigate();
  const [groupName, setGroupName] = useState("Hotel Group");
  const [logo, setLogo] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [queueSize, setQueueSize] = useState(0);
  const [order, setOrder] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const params = user?.role ? { user_role: user.role } : {};
    api.get("/settings/platform/public", { params }).then((r) => {
      if (r.data?.group_name) setGroupName(r.data.group_name);
      if (r.data?.logo_url) setLogo(r.data.logo_url);
      if (Array.isArray(r.data?.menu_order)) setOrder(r.data.menu_order);
    }).catch(() => {});
    setQueueSize(getOfflineQueueSize());
    const on = () => { setOnline(true); flushOfflineQueue(); };
    const off = () => setOnline(false);
    const onQueue = (e) => setQueueSize(e.detail?.size ?? getOfflineQueueSize());
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("pms:queue", onQueue);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("pms:queue", onQueue);
    };
  }, [user?.role]);

  const visible = items.filter((i) => {
    // Admin sees everything
    if (user?.role === "admin") return true;
    // Restaurant POS: legacy can_access_pos toggle still respected as a hard override
    if (i.to === "/pos" && user && user.role !== "manager" && user.can_access_pos === false) return false;
    // Effective section permission from PermissionProvider — hide when level == 'none'
    const level = permSections?.[i.section];
    if (level === "none") return false;
    return true;
  });
  // Apply the admin-configured sidebar order if present
  const ordered = order.length ? [...visible].sort((a, b) => {
    const ai = order.indexOf(a.section);
    const bi = order.indexOf(b.section);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  }) : visible;

  return (
    <>
      {/* Mobile top bar with hamburger — only on <md */}
      <div className="md:hidden sticky top-0 z-40 h-14 flex items-center justify-between px-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt="" className="h-8 w-8 object-contain rounded-full border border-gold/50 bg-card p-0.5" />
          ) : (
            <CrownSimpleIcon size={22} weight="duotone" className="text-gold" />
          )}
          <span className="font-display text-sm">{groupName}</span>
        </div>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="p-2 rounded-md hover:bg-muted"
          data-testid="mobile-sidebar-toggle"
          aria-label="Toggle menu"
        >
          <span className="block w-5 h-0.5 bg-foreground mb-1"></span>
          <span className="block w-5 h-0.5 bg-foreground mb-1"></span>
          <span className="block w-5 h-0.5 bg-foreground"></span>
        </button>
      </div>
      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
      )}
      <aside
        data-testid={NAV.sidebar}
        className={`${mobileOpen ? "fixed left-0 top-0 z-50 shadow-2xl flex" : "hidden"} md:sticky md:top-0 md:flex md:z-auto md:shadow-none w-64 shrink-0 h-screen max-h-screen border-r border-border bg-card flex-col md:flex overflow-hidden`}
      >
        <div className="p-6 border-b border-border shrink-0">
          <div className="flex items-center gap-3 mb-6">
            {logo ? (
              <img src={logo} alt="" className="h-10 w-10 object-contain rounded-full border border-gold/50 bg-card p-0.5 shrink-0" />
            ) : (
              <div className="h-10 w-10 rounded-full border border-gold/60 flex items-center justify-center text-gold shrink-0">
                <CrownSimpleIcon size={20} weight="duotone" />
              </div>
            )}
          <div className="min-w-0">
            <p className="font-display text-[15px] leading-tight tracking-tight truncate">{groupName}</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">PMS · Sikkim</p>
          </div>
        </div>
        <div className="prayer-flags w-14 mb-4 opacity-60" />
        <p className="label-overline mb-2">Property</p>
        <Select value={activeId} onValueChange={setActive}>
          <SelectTrigger data-testid={NAV.propertySwitcher} className="w-full">
            <SelectValue placeholder="Select property" />
          </SelectTrigger>
          <SelectContent>
            {properties.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-4 px-3 space-y-1" data-testid="sidebar-nav-scroll">
        {ordered.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.tid}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              {it.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4 border-t border-border shrink-0">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.avatar} alt={user?.name} />
            <AvatarFallback>{user?.name?.[0] || "?"}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{user?.role?.replace("_", " ")}</p>
          </div>
          <button
            onClick={toggle}
            className="p-2 rounded-md hover:bg-muted"
            title={theme === "dark" ? "Switch to light" : "Switch to twilight"}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <SunIcon size={16} /> : <MoonStarsIcon size={16} />}
          </button>
          <button
            className={`p-2 rounded-md relative hover:bg-muted ${online ? "text-[hsl(var(--success))]" : "text-destructive"}`}
            title={online ? (queueSize > 0 ? `Online — syncing ${queueSize} queued change${queueSize === 1 ? "" : "s"}…` : "Online — changes save instantly · Click to see queue") : `Offline — ${queueSize} queued change${queueSize === 1 ? "" : "s"} · Click to see queue`}
            data-testid="online-status"
            onClick={async () => {
              const items = await outboxAll();
              if (items.length === 0) {
                if (online) { flushOfflineQueue(); toast.success("All changes synced ✓"); return; }
                toast.info(`You're offline. New changes will queue and sync when back online.`);
                return;
              }
              const list = items.slice(0, 12).map((i) => `${i.method.toUpperCase()} ${i.url}`).join("\n");
              const extra = items.length > 12 ? `\n…and ${items.length - 12} more` : "";
              toast.info(`${items.length} pending change${items.length === 1 ? "" : "s"}:\n${list}${extra}`, { duration: 8000 });
              if (online) flushOfflineQueue();
            }}
          >
            {online ? <CloudArrowUpIcon size={16} weight="duotone" /> : <CloudSlashIcon size={16} weight="duotone" />}
            {queueSize > 0 && (
              <span className="absolute -top-1 -right-1 bg-[hsl(var(--warning))] text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1" data-testid="offline-queue-count">
                {queueSize}
              </span>
            )}
          </button>
          <button
            onClick={() => { logout(); nav("/login"); }}
            className="p-2 rounded-md hover:bg-muted"
            data-testid={AUTH.logoutBtn}
            aria-label="Sign out"
          >
            <SignOutIcon size={16} />
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}
